// Cookly — content seed (Italian) for editorial commerce site
// Commerce placeholders: replace #product-LINK with real product URLs.

import { toIsoDate } from "@/lib/seo";

export const BRAND = {
  name: "Cookly",
  tagline: "Meno utensili. Più pasti facili.",
  claim: "Gadget da cucina utili. Zero junk.",
  commerceTag: process.env.REACT_APP_COMMERCE_TAG || "",
};

export const NAV = [
  { label: "Guide", to: "/guide" },
  { label: "Gadget", to: "/gadget" },
  { label: "Metodo", to: "/metodo" },
  { label: "Chi sono", to: "/chi-sono" },
];

export const CATEGORIES = [
  { slug: "air-fryer", label: "Air fryer", desc: "Accessori e ricambi che valgono davvero." },
  { slug: "prep-taglio", label: "Prep & taglio", desc: "Coltelli, taglieri, mise en place." },
  { slug: "cottura-forno", label: "Cottura & forno", desc: "Teglie, stampi e utensili base." },
  { slug: "organizzazione", label: "Organizzazione cucina", desc: "Cassetti che smettono di essere un cimitero." },
  { slug: "under-30", label: "Under €30", desc: "Piccola spesa, grande resa." },
  { slug: "bundle", label: "Bundle consigliati", desc: "3 oggetti che stanno bene insieme." },
];

export const MANIFESTO = [
  {
    n: "01",
    title: "Testiamo, non ripetiamo comunicati.",
    body: "Ogni prodotto entra in cucina per almeno due settimane. Se non lo useremmo ancora tra un mese, non è qui.",
  },
  {
    n: "02",
    title: "Anti-hype per principio.",
    body: "Ci sono gadget che fanno solo bella figura nei reel. Diciamo quali. Con nome e cognome del difetto.",
  },
  {
    n: "03",
    title: "Prezzo giusto, non solo prezzo basso.",
    body: "Fascia €20–150. Preferiamo pagare 40 € una volta che 15 € tre volte.",
  },
  {
    n: "04",
    title: "Link commerciali. Trasparenza sempre.",
    body: "Alcuni link sono commerciali: potremmo ricevere una commissione o vendere direttamente. Il prezzo per te non cambia.",
  },
];

// ————————————————————————————————————————————————————————————————
// 5 GUIDES (blog)
// ————————————————————————————————————————————————————————————————
const GUIDE_SEED = [
  {
    slug: "air-fryer-per-principianti",
    category: "Air fryer",
    title: "Air fryer per principianti: cosa comprare davvero il primo mese",
    excerpt:
      "Non ti serve la macchina più grande. Ti serve la teglia giusta, tre accessori e un termometro da 12 €.",
    readMin: 7,
    date: "12 Dicembre 2025",
    cover:
      "https://images.unsplash.com/photo-1745846664210-756817e1b19c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    alt: "Air fryer nera sul piano cucina accanto a pomodori freschi e utensili",
    body: [
      "Se hai appena portato a casa un'air fryer, la prima settimana è entusiasmo puro. La seconda arriva la domanda vera: quali accessori valgono i soldi e quali finiscono in fondo al cassetto?",
      "In questa guida raccogliamo l'essenziale, testato per 30 giorni consecutivi, senza fronzoli.",
      "Regola numero uno: il cestello di serie basta e avanza per 8 ricette su 10. Il resto si risolve con carta forno forata, una pinza lunga e un termometro istantaneo.",
    ],
  },
  {
    slug: "meal-prep-30-minuti",
    category: "Meal prep",
    title: "Meal prep in 30 minuti: la mise en place che tiene 4 giorni",
    excerpt:
      "Non serve il TikTok da 40 contenitori. Serve un tagliere pesante, un coltello affilato e due ciotole giuste.",
    readMin: 6,
    date: "05 Dicembre 2025",
    cover:
      "https://images.pexels.com/photos/4252133/pexels-photo-4252133.jpeg?auto=compress&cs=tinysrgb&h=1080&w=1600",
    alt: "Mise en place: verdure tagliate in ciotole e broccoli sul tagliere",
    body: [
      "La verità del meal prep è che il 70% del tempo è taglio. Se il coltello scivola, se il tagliere balla, ci metti il doppio.",
      "Ti proponiamo un flusso da 30 minuti reali per uscirne con 4 giorni di pranzo pronto.",
      "Fascia di spesa: sotto i 100 €, una volta sola, dura anni.",
    ],
  },
  {
    slug: "organizzazione-cassetti-cucina",
    category: "Organizzazione",
    title: "Cassetti che finalmente hanno un senso: 5 organizer non finti",
    excerpt:
      "Bambù, silicone alimentare, PET riciclato. Quali reggono e quali dopo tre lavaggi diventano tristi.",
    readMin: 8,
    date: "28 Novembre 2025",
    cover:
      "https://images.unsplash.com/photo-1678108040468-0cc9addd984d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    alt: "Cassetto cucina organizzato con divisori in legno e utensili",
    body: [
      "Non c'è niente di più deprimente di un cassetto pieno di divisori economici che si spostano ogni volta che lo apri.",
      "Abbiamo passato al setaccio 12 organizer, tenuto 5, e ti spieghiamo perché.",
    ],
  },
  {
    slug: "errori-gadget-inutili",
    category: "Anti-hype",
    title: "6 gadget che tutti comprano e nessuno usa dopo 2 settimane",
    excerpt:
      "Sbucciatore multi-lame, spiralizzatore, macchina per l'hamburger. Ti diciamo cosa evitare — e cosa mettere al loro posto.",
    readMin: 5,
    date: "20 Novembre 2025",
    cover:
      "https://images.unsplash.com/photo-1678108429557-546cf9a84c35?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    alt: "Cassetto cucina pieno di utensili e gadget: il cimitero delle cose inutili",
    body: [
      "Il cimitero dei gadget da cucina è pieno di oggetti che hanno un problema comune: risolvono una cosa che non era un problema.",
      "Ecco i sei più regalati, e le alternative che davvero cambiano la giornata.",
    ],
  },
  {
    slug: "starter-cucina-sotto-100",
    category: "Bundle",
    title: "Starter cucina sotto €100: la lista definitiva per casa nuova",
    excerpt:
      "Chi si trasferisce non ha bisogno di 40 pezzi. Ne bastano 9 fatti bene, sotto i cento euro totali.",
    readMin: 9,
    date: "10 Novembre 2025",
    cover:
      "https://images.unsplash.com/photo-1556912173-46c336c7fd55?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    alt: "Cucina attrezzata per casa nuova: pentole sul piano e mixer in contesto",
    body: [
      "Ti sei trasferito o stai regalando un kit a chi lo ha fatto. Non serve comprare tutto: bastano 9 oggetti fatti bene.",
      "In questo bundle stanno un coltello vero, un tagliere pesante, un set base di pentole e tre accessori che non litigano con nessun'altra cucina.",
    ],
  },
];

/**
 * Metadati editoriali consumati dallo strato SEO.
 * `publishedAt` deriva dalla data redazionale italiana; `updatedAt` e `author`
 * restano null perché il seed non contiene revisioni né firme reali.
 */
export const GUIDES = GUIDE_SEED.map((guide) => ({
  ...guide,
  publishedAt: toIsoDate(guide.date),
  updatedAt: null,
  author: null,
}));

// ————————————————————————————————————————————————————————————————
// 5 PRODUCT ROUNDUPS (commerce)
// ————————————————————————————————————————————————————————————————
const ROUNDUP_SEED = [
  {
    slug: "accessori-air-fryer-essenziali",
    categorySlug: "air-fryer",
    category: "Air fryer",
    title: "Accessori Air Fryer essenziali",
    subtitle: "5 pezzi che usi davvero. Il resto lascialo a scaffale.",
    price: 39,
    priceRange: "€24 – €48",
    utilityScore: 9,
    hypeScore: 3,
    image:
      "https://images.unsplash.com/photo-1745846664210-756817e1b19c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Air fryer nera sul piano cucina accanto a pomodori freschi e utensili",
    forWho: "Chi ha comprato un'air fryer 5.5L+ e vuole coprire l'80% delle ricette senza spendere altri 200 €.",
    forWhoNot: "Chi usa l'air fryer una volta al mese: bastano il cestello e la carta forno forata.",
    pros: [
      "Carta forno forata: fine dei tuorli attaccati.",
      "Pinza lunga in silicone: zero graffi al rivestimento.",
      "Termometro istantaneo sotto i 20 €: cambia il gioco sul pollo.",
    ],
    cons: [
      "Alcuni ricambi sono universali solo sulla carta.",
      "Il divisore doppio riduce il volume utile.",
    ],
    avoidIf: [
      "Il tuo cestello è < 4L: gli accessori multi-livello non ci entrano.",
      "Cerchi ricambi originali del brand: qui parliamo di terze parti buone, non identiche.",
    ],
    affiliate: "#product-LINK",
  },
  {
    slug: "utensili-daily-driver",
    categorySlug: "prep-taglio",
    category: "Prep & taglio",
    title: "Utensili daily-driver",
    subtitle: "Il coltello, il tagliere, la spatola. Tre oggetti, dieci anni di vita.",
    price: 89,
    priceRange: "€72 – €118",
    utilityScore: 10,
    hypeScore: 2,
    image:
      "https://images.pexels.com/photos/4252137/pexels-photo-4252137.jpeg?auto=compress&cs=tinysrgb&h=1080&w=1400",
    alt: "Prep in cucina: coltello e tagliere con peperoni in contesto di lavoro",
    forWho: "Chi cucina 4+ volte a settimana e vuole tre oggetti che non lo tradiscano.",
    forWhoNot: "Chi ama collezionare 12 coltelli diversi: qui si punta al minimalismo funzionale.",
    pros: [
      "Coltello santoku 18 cm bilanciato, affilatura facile.",
      "Tagliere in legno end-grain: silenzioso, non spunta la lama.",
      "Spatola silicone rigida — la stessa per padella e frusta.",
    ],
    cons: [
      "Il tagliere pesante è, appunto, pesante.",
      "Il santoku non è per chi taglia molte ossa (usa un trinciante).",
    ],
    avoidIf: [
      "Preferisci coltelli con manico in plastica soft-grip: qui è legno pieno.",
      "Non vuoi ungere il tagliere una volta al mese.",
    ],
    affiliate: "#product-LINK",
  },
  {
    slug: "organizer-cassetti-cucina",
    categorySlug: "organizzazione",
    category: "Organizzazione",
    title: "Organizer cassetti cucina",
    subtitle: "Cassetti che si aprono con serenità. Bambù, non plastica finta.",
    price: 34,
    priceRange: "€22 – €58",
    utilityScore: 8,
    hypeScore: 4,
    image:
      "https://images.unsplash.com/photo-1678108040468-0cc9addd984d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Cassetto cucina organizzato con divisori in legno e utensili",
    forWho: "Chi apre il cassetto e non trova il pelapatate. Ogni volta.",
    forWhoNot: "Chi ha cassetti sotto i 40 cm di profondità: molti kit sono pensati per Ikea Metod standard.",
    pros: [
      "Divisori estensibili: si adattano alla profondità reale.",
      "Bambù trattato: regge l'acqua e si pulisce facilmente.",
      "Design piatto: non ruba centimetri utili.",
    ],
    cons: [
      "Se i cassetti hanno il fondo morbido, i divisori scivolano un po'.",
      "Non lavabile in lavastoviglie.",
    ],
    avoidIf: [
      "Ami plastica trasparente da youtuber: non fa per te.",
      "Il tuo cassetto è più profondo di 55 cm.",
    ],
    affiliate: "#product-LINK",
  },
  {
    slug: "cottura-forno-base",
    categorySlug: "cottura-forno",
    category: "Cottura & forno",
    title: "Cottura & forno — 4 pezzi base",
    subtitle: "Teglia pesante, silicone, termometro. Il resto è narrativa.",
    price: 64,
    priceRange: "€48 – €82",
    utilityScore: 9,
    hypeScore: 3,
    image:
      "https://images.unsplash.com/photo-1578338244258-d8a4e346eb47?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Teglia da forno con broccoli, zucca e carote pronti per la cottura",
    forWho: "Chi inforna almeno una volta a settimana: verdure, focacce, pollo.",
    forWhoNot: "Chi cuoce in forno una volta al mese: basta la teglia in dotazione.",
    pros: [
      "Teglia acciaio pesante che non si deforma a 220°C.",
      "Tappetino silicone: 5 anni, si lava con l'acqua.",
      "Termometro forno vero: scopri quanto sballa il tuo forno di casa (spoiler: parecchio).",
    ],
    cons: [
      "Il silicone di qualità occupa un po' di spazio.",
      "La teglia pesante è pesante — di nuovo, non un difetto.",
    ],
    avoidIf: [
      "Vuoi teglie usa-e-getta: filosofia opposta.",
      "Hai un forno microcombinato piccolo: verifica le misure.",
    ],
    affiliate: "#product-LINK",
  },
  {
    slug: "under-30-vale-i-soldi",
    categorySlug: "under-30",
    category: "Under €30",
    title: "Under €30 che valgono i soldi",
    subtitle: "Piccola spesa, grande resa. Testati per due mesi.",
    price: 27,
    priceRange: "€8 – €29",
    utilityScore: 8,
    hypeScore: 4,
    image:
      "https://images.unsplash.com/photo-1556909211-36987daf7b4d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Piccoli utensili utili sul piano: spatole, taglieri e ricettario in cucina",
    forWho: "Chi vuole regalare qualcosa di utile senza esagerare col budget.",
    forWhoNot: "Chi cerca l'oggetto premium: qui il valore è nel rapporto qualità/prezzo.",
    pros: [
      "Pelapatate a Y con lama in ceramica: taglia veramente.",
      "Frusta a filo rigido: monta la panna in 90 secondi.",
      "Contenitori in vetro con coperchio in bambù: microonde e forno tranquilli.",
    ],
    cons: [
      "Nessuno è il migliore assoluto della categoria.",
      "Vita utile 3-5 anni, non 15.",
    ],
    avoidIf: [
      "Cerchi l'heirloom di famiglia: qui parliamo di valore quotidiano.",
      "Non ti piace il legno di bambù (i coperchi lo sono).",
    ],
    affiliate: "#product-LINK",
  },
];

/** Le scorecard non hanno data di pubblicazione né firma nel seed: nessun dato inventato. */
export const ROUNDUPS = ROUNDUP_SEED.map((roundup) => ({
  ...roundup,
  publishedAt: null,
  updatedAt: null,
  author: null,
}));

// ————————————————————————————————————————————————————————————————
// BUNDLES
// ————————————————————————————————————————————————————————————————
export const BUNDLES = [
  {
    slug: "starter-cucina-100",
    title: "Starter Cucina — sotto €100",
    subtitle: "3 oggetti. Un investimento. Casa nuova, cucina già seria.",
    items: [
      { name: "Tagliere end-grain 38×28 cm", price: 39, affiliate: "#product-LINK" },
      { name: "Coltello santoku 18 cm", price: 42, affiliate: "#product-LINK" },
      { name: "Spatola silicone rigida", price: 12, affiliate: "#product-LINK" },
    ],
  },
  {
    slug: "air-fryer-mvp",
    title: "Air Fryer — MVP Kit",
    subtitle: "L'80% delle ricette con 3 accessori che stanno insieme.",
    items: [
      { name: "Carta forno forata (100 fogli)", price: 11, affiliate: "#product-LINK" },
      { name: "Pinza silicone lunga 30 cm", price: 9, affiliate: "#product-LINK" },
      { name: "Termometro istantaneo", price: 19, affiliate: "#product-LINK" },
    ],
  },
];

export const MARQUEE_PHRASES = [
  "air fryer",
  "utensili essenziali",
  "meal prep",
  "cassetti cucina",
  "accessori forno",
  "prep & taglio",
  "bundle cucina",
  "sotto €30",
  "cottura veloce",
  "organizer cucina",
  "gadget utili",
  "mise en place",
];

export const METHOD_STEPS = [
  { n: "01", title: "Selezione", body: "Solo prodotti 4★+ con spedizione veloce, fascia €20–150, disponibilità continuativa." },
  { n: "02", title: "Test reale", body: "Minimo 14 giorni di uso quotidiano in una cucina non finta. Foto vere." },
  { n: "03", title: "Score", body: "Assegniamo due punteggi: Utilità (quanto lo usi) e Hype (quanto è sopravvalutato)." },
  { n: "04", title: "Scarto", body: "Se il rapporto Utilità/Hype non tiene, non lo pubblichiamo. Punto." },
];
